import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useState } from 'react';
import { admin, impersonation as impersonationApi, type ImpersonationCandidate, type UserAdmin } from '@/api/endpoints';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from '@/components/Icon';
import { Pagination, usePaginated } from '@/components/Pagination';
import { SearchableSelect } from '@/components/SearchableSelect';
import { STUDY_TYPE_GROUPS } from '@/lib/studyTypeGroups';
import { hasPermission } from '@/types';

const ROLE: { name: string; label: string }[] = [
  { name: 'OWNER', label: 'Właściciel' },
  { name: 'GEOLOGIST', label: 'Geolog' },
  { name: 'ASSISTANT', label: 'Asystent' },
  { name: 'ENV_SPECIALIST', label: 'Specjalista ds. środowiska' },
  { name: 'OFFICE', label: 'Biuro' },
  { name: 'ADMIN', label: 'Admin' },
];

const KAT: { name: string; label: string }[] = [
  { name: 'NONE', label: 'Brak' },
  { name: 'KAT_II', label: 'Kat. II' },
  { name: 'KAT_III', label: 'Kat. III' },
  { name: 'KAT_IV', label: 'Kat. IV' },
  { name: 'KAT_V', label: 'Kat. V' },
];

export function UserManagement() {
  const { user: zalogowany } = useAuth();
  const [users, setUsers] = useState<UserAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [editing, setEditing] = useState<UserAdmin | null>(null);
  const [creating, setCreating] = useState(false);
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [wcielenie, setWcielenie] = useState<UserAdmin | null>(null);
  /** Ocena „w kogo mogę wejść" liczona przez backend (macierz uprawnień), po id użytkownika. */
  const [kandydaci, setKandydaci] = useState<Map<number, ImpersonationCandidate>>(new Map());

  const mozeWcielac = hasPermission(zalogowany, 'ADMIN_IMPERSONATE');

  useEffect(() => { void load(); }, []);
  // Osobny effect, bo uprawnienia z AuthContext mogą dojechać po pierwszym renderze.
  useEffect(() => { void wczytajKandydatow(); }, [mozeWcielac]);

  async function wczytajKandydatow() {
    if (!mozeWcielac) return;
    try {
      const list = await impersonationApi.candidates();
      setKandydaci(new Map(list.map(k => [k.id, k])));
    } catch { /* brak oceny = przycisk po prostu się nie pojawi */ }
  }

  async function load() {
    try {
      setUsers(await admin.users.list());
      // Ocena kandydatów odświeża się razem z listą kont — inaczej użytkownik dodany przed
      // chwilą nie dostawałby przycisku „Pracuj jako" aż do przeładowania strony.
      await wczytajKandydatow();
    } catch (e: any) { setMsg({ type: 'err', text: e.message }); }
    finally { setLoading(false); }
  }

  async function blokuj(u: UserAdmin) {
    const powod = window.prompt('Powód blokady konta:');
    if (powod === null) return;
    try {
      await admin.users.block(u.id, powod);
      setMsg({ type: 'ok', text: 'Konto zablokowane' });
      await load();
    } catch (e: any) { setMsg({ type: 'err', text: e.message }); }
  }

  async function odblokuj(u: UserAdmin) {
    if (!confirm('Odblokować konto ' + u.login + '?')) return;
    try {
      await admin.users.unblock(u.id);
      setMsg({ type: 'ok', text: 'Konto odblokowane' });
      await load();
    } catch (e: any) { setMsg({ type: 'err', text: e.message }); }
  }

  async function reset(u: UserAdmin) {
    if (!confirm('Zresetować hasło dla ' + u.login + '?')) return;
    try {
      const r = await admin.users.resetPassword(u.id);
      setResetResult('Nowe hasło dla ' + u.login + ':\n\n' + r.password + '\n\nSkopiuj je — nie będzie ponownie widoczne.');
      await load();
    } catch (e: any) { setMsg({ type: 'err', text: e.message }); }
  }

  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(users, { storageKey: 'users' });

  if (loading) return <div className="text-ink-400">Ładowanie…</div>;

  return (
    <div className="max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Zarządzanie użytkownikami</h1>
          <div className="text-ink-400 text-[13px]">{users.length} kont · zarządzanie rolami i kategoriami uprawnień</div>
        </div>
        <button onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
          <Icon name="plus" className="w-3.5 h-3.5" /> Nowe konto
        </button>
      </header>

      {msg && <div className={'mb-4 p-3 rounded text-[13px] ' + (msg.type === 'ok' ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500')}>{msg.text}</div>}
      {resetResult && (
        <div className="mb-4 p-3 rounded bg-amber-100 text-ink-800 font-mono text-[12px] whitespace-pre-wrap">
          {resetResult}
          <button onClick={() => setResetResult(null)} className="btn btn-secondary btn-sm mt-2">OK, zapisałem</button>
        </div>
      )}

      <div className="card overflow-hidden">
        {/* Tabela jest szersza niż karta (9 kolumn + 4 akcje), a `overflow-hidden` na karcie
            ucinał skrajne przyciski bez możliwości dojechania do nich. Własny kontener
            z poziomym scrollem zamiast obcinania. */}
        <div className="overflow-x-auto">
        <table className="table-hw">
          <thead>
            <tr>
              <th>Login</th><th>Imię i nazwisko</th><th>E-mail</th>
              <th>Role</th><th>Kategoria</th><th>2FA</th><th>Status</th>
              <th>Ostatnie logowanie</th><th></th>
            </tr>
          </thead>
          <tbody>
            {pagedItems.map(u => (
              <tr key={u.id}>
                <td className="font-mono text-[12px]">{u.login}</td>
                <td>
                  <div>{u.fullName}</div>
                  {u.certificateNumber && <div className="text-[11px] text-ink-400">{u.certificateNumber}</div>}
                </td>
                <td className="text-[12px]">{u.email}</td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(u.roles ?? []).map(r => <span key={r} className="pill pill-water text-[10px]">{r}</span>)}
                  </div>
                  {(u.studyTypes ?? []).length > 0 && (
                    <div className="text-[11px] text-ink-500 mt-1" title="Typy opracowań — zadania do przyjęcia z tablicy">
                      {STUDY_TYPE_GROUPS.filter(g => g.types.some(t => u.studyTypes!.includes(t))).map(g => g.key).join(' · ')}
                    </div>
                  )}
                </td>
                <td>
                  <span className="pill text-[11px]">{u.category}</span>
                </td>
                <td>
                  {u.totpEnabled ? <span className="pill pill-green text-[10px]">✓ TOTP</span>
                                 : <span className="pill text-[10px]">brak</span>}
                </td>
                <td>
                  {u.active ? <span className="pill pill-green text-[10px]">Aktywne</span>
                             : <span className="pill pill-red text-[10px]" title={u.lockReason}>Zablokowane</span>}
                </td>
                <td className="text-[11px] font-mono text-ink-500">
                  {u.lastLogin ? new Date(u.lastLogin).toLocaleString('pl-PL') : '—'}
                </td>
                <td className="text-right">
                  <div className="flex gap-1 justify-end">
                    {mozeWcielac && kandydaci.has(u.id) && (
                      <button
                        onClick={() => setWcielenie(u)}
                        disabled={!kandydaci.get(u.id)!.dozwolone}
                        title={kandydaci.get(u.id)!.powodBlokady
                          ?? `Zobacz portal oczami ${u.fullName}`}
                        className="btn btn-ghost btn-sm text-water-700 disabled:opacity-40">
                        Pracuj jako
                      </button>
                    )}
                    {u.zarzadzalne ? (
                      <>
                        <button onClick={() => setEditing(u)} className="btn btn-ghost btn-sm">Edytuj</button>
                        <button onClick={() => void reset(u)} className="btn btn-ghost btn-sm">Reset hasła</button>
                        {u.active
                          ? <button onClick={() => void blokuj(u)} className="btn btn-ghost btn-sm text-red-500">Blokuj</button>
                          : <button onClick={() => void odblokuj(u)} className="btn btn-ghost btn-sm text-green-500">Odblokuj</button>}
                      </>
                    ) : (
                      <span className="text-[11px] text-ink-400 px-2"
                            title="To konto ma uprawnienia, których nie masz — administrowanie nim wymaga wyższych uprawnień.">
                        poza Twoim zasięgiem
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="px-3 border-t border-ink-100">
          <Pagination total={total} page={page} pageSize={pageSize}
                      onPageChange={setPage} onPageSizeChange={setPageSize}/>
        </div>
      </div>

      {creating && <UserForm onClose={() => { setCreating(false); void load(); }} />}
      {editing && <UserForm user={editing} onClose={() => { setEditing(null); void load(); }} />}
      {wcielenie && <ImpersonateModal user={wcielenie} onClose={() => setWcielenie(null)} />}
    </div>
  );
}

/**
 * Potwierdzenie wejścia w cudze konto. Prosimy o hasło ADMINISTRATORA, bo API jest zwolnione
 * z CSRF — bez tego kroku samo przejęte ciasteczko sesji wystarczyłoby, by wejść w dowolne konto.
 */
function ImpersonateModal({ user, onClose }: { user: UserAdmin; onClose: () => void }) {
  const { wcielSie } = useAuth();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [blad, setBlad] = useState<string | null>(null);

  async function potwierdz(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setBlad(null);
    try {
      await wcielSie(user.id, password);
      // Twarde przejście na start portalu: widoki mają już w pamięci dane wczytane
      // na uprawnieniach administratora, a od tej chwili sesja ma uprawnienia użytkownika.
      window.location.assign('/');
    } catch (e: any) {
      setBusy(false);
      setBlad(e?.body?.message ?? e?.body?.error ?? 'Nie udało się wcielić w tego użytkownika.');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={potwierdz}
            className="card p-5 w-full max-w-[460px] bg-white">
        <h2 className="text-[16px] font-semibold mb-1">Pracuj jako {user.fullName}</h2>
        <p className="text-[13px] text-ink-500 mb-3">
          Portal przełączy się na uprawnienia konta <span className="font-mono">{user.login}</span>.
          Zobaczysz dokładnie to, co widzi ten użytkownik.
        </p>
        <div className="text-[12px] bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 mb-3">
          Wszystko, co zapiszesz w tym trybie, zapisze się na jego konto. Audyt odnotuje, że
          zrobiłeś to Ty. Nie będziesz mógł zmienić jego hasła ani ustawień 2FA.
        </div>
        <label className="block text-[12px] font-medium text-ink-600 mb-1">
          Potwierdź swoim hasłem
        </label>
        <input type="password" value={password} autoFocus required
               onChange={e => setPassword(e.target.value)}
               className="input w-full mb-3" placeholder="Twoje hasło" />
        {blad && <div className="text-[12px] text-red-600 mb-3">{blad}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Anuluj</button>
          <button type="submit" disabled={busy || !password} className="btn btn-primary btn-sm">
            {busy ? 'Przełączam…' : 'Wejdź na konto'}
          </button>
        </div>
      </form>
    </div>
  );
}

function UserForm({ user, onClose }: { user?: UserAdmin; onClose: () => void }) {
  // Parytet z UserAdminApi: zmiana zestawu ról wymaga ADMIN_ROLE_ASSIGN, samo ADMIN_USERS
  // (które otwiera ten formularz) już nie wystarcza.
  const { user: zalogowany } = useAuth();
  const mozeNadawacRole = hasPermission(zalogowany, 'ADMIN_ROLE_ASSIGN');
  const isEdit = !!user;
  const [login, setLogin] = useState(user?.login ?? '');
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [roles, setRoles] = useState<string[]>(user?.roles ?? []);
  const [category, setCategory] = useState(user?.category ?? 'NONE');
  const [swiadectwo, setSwiadectwo] = useState(user?.certificateNumber ?? '');
  const [studyTypes, setStudyTypes] = useState<string[]>(user?.studyTypes ?? []);
  const [password, setPassword] = useState('');
  const [wymagajZmiany, setWymagajZmiany] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Role z katalogu (dynamiczne) — fallback do listy wbudowanej, gdy API niedostępne.
  const [available, setAvailable] = useState<{ name: string; label: string; active: boolean }[]>([]);
  useEffect(() => {
    admin.permissions.roles()
      .then(rs => setAvailable(rs.map(r => ({ name: r.name, label: r.displayName, active: r.active }))))
      .catch(() => { /* brak dostępu do katalogu ról — użyjemy listy wbudowanej */ });
  }, []);
  const roleChoices = available.length
    ? available.filter(r => r.active || roles.includes(r.name))
    : ROLE.map(r => ({ ...r, active: true }));

  async function save() {
    setBusy(true); setErr(null);
    try {
      if (isEdit) {
        await admin.users.update(user!.id, { firstName, lastName, email, roles, category, certificateNumber: swiadectwo, studyTypes });
      } else {
        if (password.length < 8) { setErr('Hasło musi mieć min 8 znaków'); setBusy(false); return; }
        await admin.users.create({
          login, firstName, lastName, email, roles, category,
          certificateNumber: swiadectwo, studyTypes, password, wymagajZmianyHasla: wymagajZmiany,
        });
      }
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Błąd zapisu');
    } finally { setBusy(false); }
  }

  function toggleRole(r: string) {
    setRoles(roles.includes(r) ? roles.filter(x => x !== r) : [...roles, r]);
  }

  /** Grupa zaznaczona w całości → odznacz całą; inaczej (nic albo część) → zaznacz całą. */
  function toggleStudyGroup(types: string[]) {
    const all = types.every(t => studyTypes.includes(t));
    setStudyTypes(all ? studyTypes.filter(t => !types.includes(t)) : [...new Set([...studyTypes, ...types])]);
  }

  const { requestClose, confirmation, dialogProps } = useUnsavedChanges({
    value: { login, firstName, lastName, email, roles, studyTypes, category, swiadectwo, password, wymagajZmiany }, onClose, busy,
  });

  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center z-50 p-5" {...dialogProps}>
      {confirmation}
      <div className="card max-w-[600px] w-full">
        <div className="card-head">
          <div className="card-title">{isEdit ? 'Edycja użytkownika' : 'Nowy użytkownik'}</div>
          <button onClick={requestClose} className="ml-auto btn btn-ghost btn-icon"><Icon name="x" /></button>
        </div>
        <div className="card-body space-y-3">
          {err && <div className="p-2 bg-red-100 text-red-500 rounded text-[12px]">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Login</span>
              <input value={login} onChange={e => setLogin(e.target.value)} disabled={isEdit} className="input" />
            </label>
            <label className="field">
              <span className="field-label">E-mail</span>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input" />
            </label>
            <label className="field">
              <span className="field-label">Imię</span>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} className="input" />
            </label>
            <label className="field">
              <span className="field-label">Nazwisko</span>
              <input value={lastName} onChange={e => setLastName(e.target.value)} className="input" />
            </label>
          </div>

          <div className="field">
            <span className="field-label">
              Role (zaznacz wiele)
              {!mozeNadawacRole && <span className="text-ink-400 font-normal"> · tylko podgląd, brak uprawnienia „Nadawanie ról"</span>}
            </span>
            <div className="flex flex-wrap gap-2 mt-1">
              {roleChoices.map(r => (
                <label key={r.name} title={r.name} className={'px-3 py-1 rounded border text-[12px] ' +
                  (mozeNadawacRole ? 'cursor-pointer ' : 'cursor-not-allowed opacity-70 ') +
                  (roles.includes(r.name) ? 'bg-water-500 text-white border-water-500' : 'bg-white border-ink-200 text-ink-700')}>
                  <input type="checkbox" checked={roles.includes(r.name)} disabled={!mozeNadawacRole}
                    onChange={() => toggleRole(r.name)} className="hidden" />
                  {r.label}
                </label>
              ))}
            </div>
          </div>

          <div className="field">
            <span className="field-label">
              Wykonywane opracowania
              <span className="text-ink-400 font-normal"> · zadania tych typów pokażą się na tablicy do przyjęcia</span>
            </span>
            <div className="flex flex-wrap gap-2 mt-1">
              {STUDY_TYPE_GROUPS.map(g => {
                const ile = g.types.filter(t => studyTypes.includes(t)).length;
                const cale = ile === g.types.length;
                return (
                  <button key={g.key} type="button" onClick={() => toggleStudyGroup(g.types)}
                    title={ile > 0 && !cale ? `Zaznaczone ${ile} z ${g.types.length} typów — kliknij, aby zaznaczyć całą grupę` : undefined}
                    className={'px-3 py-1 rounded border text-[12px] ' +
                      (cale ? 'bg-water-500 text-white border-water-500'
                        : ile > 0 ? 'bg-water-50 border-water-500 border-dashed text-ink-700'
                        : 'bg-white border-ink-200 text-ink-700')}>
                    {g.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Kategoria uprawnień</span>
              <SearchableSelect value={category} onChange={setCategory}>
                {KAT.map(k => <option key={k.name} value={k.name}>{k.label}</option>)}
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">Nr świadectwa kwalifikacji</span>
              <input value={swiadectwo} onChange={e => setSwiadectwo(e.target.value)} className="input" placeholder="np. V/1234/2012" />
            </label>
          </div>

          {!isEdit && (
            <>
              <label className="field">
                <span className="field-label">Hasło tymczasowe (min 8 znaków)</span>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="input" />
              </label>
              <label className="flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={wymagajZmiany} onChange={e => setWymagajZmiany(e.target.checked)} />
                Wymagaj zmiany hasła przy pierwszym logowaniu
              </label>
            </>
          )}

          <div className="flex gap-2 justify-end pt-3 border-t border-ink-100">
            <button onClick={requestClose} className="btn btn-secondary btn-sm">Anuluj</button>
            <button disabled={busy} onClick={() => void save()} className="btn btn-primary btn-sm">
              {busy ? 'Zapisuję…' : (isEdit ? 'Zapisz zmiany' : 'Utwórz konto')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
